import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchBar } from './SearchBar';
import { HeroSearchBar } from './HeroSearchBar';
import { SearchModal } from './SearchModal';
import { searchContent, organizeSearchResults } from './SearchAPI';
import { SearchResult, TabType } from './types';

interface SearchProps {
  variant?: 'default' | 'hero';
}

export function Search({ variant = 'default' }: SearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const navigate = useNavigate();

  // Handle keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset search state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      // Wait a bit before resetting to allow animations to complete
      const timeout = setTimeout(() => {
        setQuery('');
        setResults([]);
        setActiveTab('all');
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  // Handle search when query changes
  useEffect(() => {
    const performSearch = async () => {
      if (!query.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const searchResults = await searchContent(query);
        setResults(searchResults);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(performSearch, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleResultClick = (result: SearchResult) => {
    setIsOpen(false);
    
    switch (result.type) {
      case 'news':
        navigate(`/noticias/${result.slug}`, { 
          state: { fromSearch: true, query } 
        });
        break;
      case 'event':
        navigate(`/eventos`, { 
          state: { fromSearch: true, selectedId: result.id, query } 
        });
        break;
      case 'service':
      case 'quicklink':
        if (result.url.startsWith('/')) {
          navigate(result.url, { 
            state: { fromSearch: true, selectedId: result.id, query } 
          });
        } else {
          window.open(result.url, '_blank');
        }
        break;
      case 'department':
        navigate(`/departamentos`, { 
          state: { fromSearch: true, selectedId: result.id, query } 
        });
        break;
      case 'document':
        if (result.subcategory?.category?.slug) {
          navigate(`/documentos/${result.subcategory.category.slug}/${result.subcategory.slug}`, {
            state: { 
              fromSearch: true, 
              selectedId: result.id,
              query,
              year: result.year,
              number: result.number
            }
          });
        } else {
          window.open(result.url, '_blank');
        }
        break;
      case 'bidding':
        navigate(`/licitacoes`, {
          state: {
            fromSearch: true,
            selectedId: result.id,
            query
          }
        });
        break;
    }
  };

  // Get filtered results based on active tab
  const filteredResults = organizeSearchResults(results, activeTab);

  return (
    <>
      {variant === 'default' ? (
        <SearchBar onOpen={() => setIsOpen(true)} />
      ) : (
        <HeroSearchBar onOpen={() => setIsOpen(true)} />
      )}
      
      <SearchModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        query={query}
        setQuery={setQuery}
        results={filteredResults}
        loading={loading}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleResultClick={handleResultClick}
      />
    </>
  );
}